/* ==========================================================================
   Rook UI Core — Carousel Controller
   Web Component: <rk-carousel-element>
   Powered by Swiper.js (Lazy Loaded via CDN)
   Hydration tier: 'visible' — Swiper bundle only downloads when the
   carousel scrolls into view.
   ========================================================================== */

import { RkComponent } from '@rook/component';

class RkCarouselElement extends RkComponent {
    static hydration = 'visible';

    #resizeObserver = null;

    setup() {
        this.swiper = null;
        this.options = {};
        this.#initialize();
    }

    async #initialize() {
        await this.#loadSwiper();
        if (!this.isConnected) return;
        this.#initSwiper();

        if (window.ResizeObserver) {
            this.#resizeObserver = new ResizeObserver(() => {
                if (this.swiper && typeof this.swiper.update === 'function') {
                    this.swiper.update();
                }
            });
            this.#resizeObserver.observe(this);
        }
    }

    teardown() {
        if (this.swiper && typeof this.swiper.destroy === 'function') {
            this.swiper.destroy(true, true);
            this.swiper = null;
        }
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = null;
    }

    async #loadSwiper() {
        if (window.Swiper) return;
        if (window.RkSwiperLoading) return window.RkSwiperLoading;

        window.RkSwiperLoading = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';

            const style = document.createElement('link');
            style.rel = 'stylesheet';
            style.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';

            script.onload = () => resolve();
            script.onerror = reject;

            document.head.appendChild(style);
            document.head.appendChild(script);
        });

        return window.RkSwiperLoading;
    }

    #initSwiper() {
        const container = this.querySelector('.swiper');
        if (!container) return;

        const rawOptions = this.getAttribute('data-swiper-options');
        if (rawOptions) {
            try {
                this.options = JSON.parse(rawOptions);
            } catch (e) {
                console.error('Invalid Swiper options JSON:', e);
            }
        }

        const gap = parseInt(this.getAttribute('data-gap')) || 0;
        const deskItems = parseFloat(this.getAttribute('data-desktop-items')) || 3;
        const mobileItems = parseFloat(this.getAttribute('data-mobile-items')) || 1;

        const defaultOptions = {
            slidesPerView: mobileItems,
            spaceBetween: gap,
            navigation: {
                nextEl: this.querySelector('.swiper-button-next'),
                prevEl: this.querySelector('.swiper-button-prev'),
            },
            pagination: {
                el: this.querySelector('.swiper-pagination'),
                clickable: true,
            },
            loop: this.hasAttribute('data-loop') && this.getAttribute('data-loop') !== 'false',
            autoplay: this.hasAttribute('data-autoplay') ? {
                delay: parseInt(this.getAttribute('data-autoplay-delay')) || 3000,
                disableOnInteraction: false,
            } : false,
            freeMode: this.hasAttribute('data-free-mode'),
            breakpoints: this.options.breakpoints || {
                768: { slidesPerView: deskItems }
            },
            a11y: {
                enabled: true,
                prevSlideMessage: 'Slide Anterior',
                nextSlideMessage: 'Próximo Slide',
            }
        };

        const finalOptions = { ...defaultOptions, ...this.options };

        this.swiper = new window.Swiper(container, finalOptions);

        this.rkEmit('carousel:init', { swiper: this.swiper });
    }

    slideNext() { if (this.swiper) this.swiper.slideNext(); }
    slidePrev() { if (this.swiper) this.swiper.slidePrev(); }
    slideTo(index) { if (this.swiper) this.swiper.slideTo(index); }
}

if (!customElements.get('rk-carousel-element')) {
    customElements.define('rk-carousel-element', RkCarouselElement);
}
